import { BrandfolderHexColors, useFetch } from '@brandfolder/react';
import { AttachmentDto } from '@brandfolder-panel/sdk';
import '@brandfolder-panel/sdk/index.css';
import { useRouter } from 'next/router';
import { useContext, useEffect, useRef } from 'react';

import { BrandguideContext } from '@context/brandguide';
import { ConfigContext } from '@context/config';
import { DragAndDropContext } from '@context/drag-and-drop';
import { redirectToSignOut } from '@utilities/oauth2';

// https://developer.mozilla.org/en-US/docs/Web/HTML/Element/img#supported_image_formats
const ALLOWED_MIME_TYPES = [
  'image/apng',
  'image/avif',
  'image/gif',
  'image/jpeg',
  'image/png',
  'image/svg+xml',
  'image/webp'
];

export interface PanelUIProps {
  onSelect: (attachment: AttachmentDto) => void;
}

export function PanelUI({ onSelect }: PanelUIProps) {
  const panelRootRef = useRef<HTMLDivElement | null>(null);

  const router = useRouter();

  const { brandguide } = useContext(BrandguideContext);
  const { publicRuntimeConfig } = useContext(ConfigContext);
  const { setDragging } = useContext(DragAndDropContext);

  const { error, response: token } = useFetch<string>({
    url: '/api/access-token'
  });

  useEffect(() => {
    if (error) {
      redirectToSignOut(router);
    }
  }, [error]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    async function setupPanel() {
      if (panelRootRef.current && brandguide?.resourceable_key && token) {
        const BrandfolderPanelSdkModule = await import('@brandfolder-panel/sdk');

        const panelUI = new BrandfolderPanelSdkModule.PanelUISdk({
          anchorElement: panelRootRef.current as HTMLElement,
          appName: 'Brandguide',
          authParameters: {
            token
          },
          environmentVariables: {
            bfApiBaseUrl: `${publicRuntimeConfig.BRANDFOLDER_BOULDER_URL}/api`
          },
          styles: {
            backgroundColor: BrandfolderHexColors.Marshmallow,
            backgroundColorSecondary: BrandfolderHexColors.DirtyMarshmallow
          },
          statePersistence: {
            enabled: true,
            storage: {
              getItem: (key: string) => Promise.resolve(sessionStorage.getItem(key)),
              setItem: (key: string, value: string) => {
                sessionStorage.setItem(key, value);
                return Promise.resolve();
              }
            }
          }
        });

        panelUI.selectAttachments({
          allowedMimeTypes: ALLOWED_MIME_TYPES,
          containerId: brandguide?.resourceable_key,
          onSelect,
          hideHeader: true,
          onDragStart: () => {
            setDragging(true);
          },
          onDragEnd: () => {
            setDragging(false);
          },
          resumeSession: true
        });
      }
    }

    setupPanel();
  }, [brandguide?.resourceable_key, token]); // eslint-disable-line react-hooks/exhaustive-deps

  return <div id="panel-root" ref={panelRootRef} style={{ width: '100%', height: '100%' }} />;
}
